/**
 * Centralized logging utility with production-aware log levels
 *
 * In production mode (NEXT_PUBLIC_DEPLOY_ENV=production):
 * - debug and info logs are suppressed
 * - warn and error logs are always shown (needed for production debugging)
 *
 * In development mode:
 * - All log levels are shown
 */

import { isProductionDeploy } from "./deploy-env";

type LogFunction = (...args: unknown[]) => void;

interface Logger {
  /**
   * Debug-level logs - suppressed in production
   * Use for detailed debugging information
   */
  debug: LogFunction;

  /**
   * Info-level logs - suppressed in production
   * Use for general informational messages
   */
  info: LogFunction;

  /**
   * Warning-level logs - always shown
   * Use for potential issues that don't prevent operation
   */
  warn: LogFunction;

  /**
   * Error-level logs - always shown
   * Use for errors that need attention
   */
  error: LogFunction;
}

// Cache the production check to avoid repeated calls
const isProduction = isProductionDeploy();

// No-op function for suppressed logs
const noop: LogFunction = () => {};

export const logger: Logger = {
  debug: isProduction ? noop : (...args: unknown[]) => console.debug(...args),
  info: isProduction ? noop : (...args: unknown[]) => console.log(...args),
  warn: (...args: unknown[]) => console.warn(...args),
  error: (...args: unknown[]) => console.error(...args),
};

export default logger;
